import { Injectable } from '@angular/core';

import { pipe, of } from 'rxjs';
import {
  map,
  mergeMap,
  take,
  catchError,
  switchMap,
  withLatestFrom,
  concatMap
} from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { RouterReducerState } from '@ngrx/router-store';

import { RouterStateUrl, ReferenceService } from '@sd-portal/shared/util';
import { State } from '..';
import * as actions from './expense.actions';

import { ExpenseAuditService } from '../../expense-audit.service';
import { EMPTY_EXPENSE_SKELETON } from '../../+models/expense.audit.model';

export const MAP_ROUTER_STORE = pipe(
  select((s: { router: RouterReducerState<RouterStateUrl> }) => s.router),
  map(r => r && r.state && r.state.params),
  take(1)
);

@Injectable()
export class ExpenseEffects {
  constructor(
    private readonly expenseAuditService: ExpenseAuditService,
    private readonly referenceService: ReferenceService,
    private readonly actions$: Actions,
    private readonly store: Store<{
      router: RouterReducerState<RouterStateUrl>;
      sharedAudit: State;
    }>
  ) {}

  loadExpenseCategoryTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadExpenseCategoryTypes),
      mergeMap(() =>
        this.referenceService.getReferenceItemsNoCache('ExpenseCategory', true).pipe(
          map(payload => actions.ExpenseCategoryTypesLoaded({ payload })),
          catchError(err => of(actions.ExpenseCategoryTypesLoadError(err)))
        )
      )
    )
  );

  loadExpenseSkeleton$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadExpenseSkeleton),
      mergeMap(() =>
        this.store.pipe(
          MAP_ROUTER_STORE,
          map(params => Number(params.id)),
          switchMap(id =>
            id ? this.expenseAuditService.getExpenseSkeleton(id) : of(EMPTY_EXPENSE_SKELETON)
          ),
          map(payload => actions.ExpenseSkeletonLoaded({ payload })),
          catchError(err => of(actions.ExpenseSkeletonLoaded(err)))
        )
      )
    )
  );

  expenseSkeletonLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.ExpenseSkeletonLoaded),
      map(action => actions.LoadExpenseAudit({ expenseId: action.payload.expenseId }))
    )
  );

  loadDeletedExpenseSkeletons$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadDeletedExpenseSkeletons),
      mergeMap(() =>
        this.expenseAuditService.getDeletedExpenseSkeletons().pipe(
          map(payload => actions.DeletedExpenseSkeletonsLoaded({ payload })),
          catchError(err => of(actions.DeletedExpenseSkeletonsLoaded(err)))
        )
      )
    )
  );

  loadExpenseAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadExpenseAudit),
      mergeMap(action =>
        this.expenseAuditService.getExpenseAudit(action.expenseId).pipe(
          map(audits => actions.ExpenseAuditLoaded({ expenseId: action.expenseId, audits })),
          catchError(err => of(actions.ExpenseAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageExpenseAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageExpenseAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.expense.expenseAudit.lastAuditLogId;
        }
        return this.expenseAuditService
          .getExpenseAudit(action.expenseId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits => actions.ExpenseAuditLoaded({ expenseId: action.expenseId, audits })),
            catchError(err => of(actions.ExpenseAuditLoadError(err)))
          );
      })
    )
  );
}
